import {
  BillingStatusLabelsKeys,
  ReservationStatusLabelsKeys,
  UpdatedByLabelsKeys,
  ImageProps,
  UploadImagesProps,
  CallMethodNames,
  SettlementNameProps,
  PharmacyProps,
  VerificationTypes,
  CallMethodVideoAppNames,
  ReservationFrameUnitMin,
  CallMethodLabels,
} from './common'
import { ConsultationMethod, PharmacyServiceType } from './applicant'

export type LoginUserProps = {
  email: string
  password: string
}

export type adminUserProps = {
  id?: number
  last_name: string
  first_name: string
  last_name_kana: string
  first_name_kana: string
  email: string
}

export type partnerProps = {
  partner_company_id?: number
  name?: string
  branch_name: string
  contact_person_name: string
  email: string
  phone: string
  remarks: string
}

type InitialSupportProps = {
  is_request: boolean
  request_datetime?: string
  email?: string
}

export type MedicalInstitutionProps = {
  id?: number
  name: string
  phone: string
  postal_code: string
  prefecture: string
  city: string
  town: string
  street: string
  building?: string
  form_url?: string
  is_active?: boolean
  is_connected_stripe?: boolean
  online_eligibility_verification_url?: string | null
  admin_user?: adminUserProps
}

export type EntryPlanCategory = {
  category: string
  plans: string[]
}

export type ApplicationEntryFormProps = MedicalInstitutionProps & {
  introduced_partner?: string
  is_applied_yadoc: boolean
  partner?: partnerProps
  application_date?: string
  initial_support?: InitialSupportProps
  plan_category: string
  plan: string
  campaign_code?: string
}

export type ApplicationEntryEditFormProps = MedicalInstitutionProps & {
  introduced_partner?: string
  is_applied_yadoc: boolean
  partner?: partnerProps
  application_date?: string
  initial_support?: InitialSupportProps
  plan_category?: string
  plan?: string
  campaign_code?: string
}

export type AdminMedicalInstitutionListItem = ApplicationEntryFormProps & {
  video_call_methods?: { name: CallMethodNames; has_connection: boolean }[]
  is_connected_stripe?: boolean
}

export type AdminMedicalInstitution = MedicalInstitutionProps & {
  introduced_partner?: string
  is_applied_yadoc: boolean
  partner: partnerProps
  application_date?: string
  initial_support?: InitialSupportProps
  plan_category?: string
  plan?: string
  campaign_code?: string
  online_eligibility_verification_url?: string | null
}

export type partnerCompanyProps = {
  id: number
  name: string
}

// 診療科目関連

export type ReservationFrameProps = {
  frame_datetime_start: string
  frame_datetime_end: string
  number_of_people: number
  reserved_people_count: number
}

export type ReservationTimeFrameProps = {
  start_time: string
  end_time: string
  number_of_people: number
}

export type RegularReservationSettingProps = {
  day_of_week: number
  reservation_frames: ReservationTimeFrameProps[]
}

export type IndividualReservationSettingProps = {
  start_date: string
  end_date: string
  reservation_frames: ReservationTimeFrameProps[]
}

export type CloseDateSettingProps = {
  close_date: string
}

export type VideoAppCallMethodProps = MethodProps & {
  name: CallMethodVideoAppNames
  identification?: string
}
export type CallMethodProps = MethodProps & {
  name: CallMethodNames
  identification?: string
}

export const SettlementsNames: {
  [key in SettlementNameProps]:
    | 'クレジットカード（Stripe決済）'
    | 'クレジットカード（リンク決済）'
    | '銀行振込'
    | '代金引換'
    | '対面時窓口'
    | '自己負担なし'
} = {
  credit_card: 'クレジットカード（Stripe決済）',
  link: 'クレジットカード（リンク決済）',
  bank_transfer: '銀行振込',
  cod: '代金引換',
  next_visit: '対面時窓口',
  no_charge: '自己負担なし',
}

export type MethodLabels =
  | 'クレジットカード（Stripe決済）'
  | 'クレジットカード（リンク決済）'
  | 'クレジットカード'
  | '銀行振込'
  | '代金引換'
  | '対面時窓口'
  | '自己負担なし'
  | 'リンク決済'
  | 'Stripe'
  | '電話'
  | 'Zoom'
  | 'Microsoft Teams'

export type MethodProps = {
  name: string
  label: MethodLabels
  is_active: boolean
}

export type SettlementSettingProps = MethodProps & {
  name: SettlementNameProps
}

export type SelectOptionsProps = 'optional' | 'required' | 'not'

export type ReservationDeadlineTimings =
  | 'just_before'
  | '30_minutes_before'
  | 'day_before'
  | 'day_before_12:00'

export type DepartmentSettingsProps = {
  id?: number
  name: string
  is_open: boolean
  display_order?: number
  supplementary_text: string
  reservable_period: number
  reservation_deadline_timing: ReservationDeadlineTimings | null
  reservation_frame_period_minutes: ReservationFrameUnitMin
  reservation_frame_start_time: string | null
  reservation_frame_end_time: string | null
  regular_reservation_settings: RegularReservationSettingProps[]
  individual_reservation_settings: IndividualReservationSettingProps[]
  close_date_settings: CloseDateSettingProps[]
  free_description_field_setting: SelectOptionsProps
  free_description_supplement: string | null
  attachment_image_setting: SelectOptionsProps
  attachment_image_supplement: string
  use_in_person_consultation: boolean
  call_methods: CallMethodProps[]
  reservation_message?: string
  information_for_reservation?: string
  applicant_address_setting: SelectOptionsProps
  registration_card_number_setting: SelectOptionsProps
  identification_document_setting: SelectOptionsProps
  insurance_card_setting: SelectOptionsProps
  certificate_setting: SelectOptionsProps
  settlement_settings: SettlementSettingProps[]
}

export const reservationDeadlineTimings: {
  [key in ReservationDeadlineTimings]:
    | '開始時刻の直前'
    | '開始時刻の30分前'
    | '開始前日23:59まで'
    | '開始前日11:59まで'
} = {
  just_before: '開始時刻の直前',
  '30_minutes_before': '開始時刻の30分前',
  day_before: '開始前日23:59まで',
  'day_before_12:00': '開始前日11:59まで',
}

export type BankTransfer = {
  bank_name: string
  bank_number: string
  branch_name: string
  branch_number: string
  account_type: '普通' | '当座' | '定期'
  account_number: string
  account_name: string
  payment_deadline: '' | '7' | '14' | '21' | '28' // NOTE: 単位はdays
}

export type ReservationListItem = {
  id: number
  applicant_last_name: string
  applicant_first_name: string
  applicant_first_name_kana?: string
  applicant_last_name_kana?: string
  applicant_age: number
  call_method?: CallMethodNames
  consultation_method?: ConsultationMethod | null
  reservation_datetime_start: string
  reservation_datetime_end: string
  reservation_department: string
  status: ReservationStatusLabelsKeys
}

export type ReservationDetailProps = {
  id: number
  access_key: string
  reservation_number: string
  reservation_department_id: number
  reservation_department_name: string
  reservation_datetime_start: string
  reservation_datetime_end: string
  free_description: string | null
  call_method?: CallMethodNames
  call_method_label?: CallMethodLabels
  consultation_method?: ConsultationMethod | null
  reservation_message?: string
  applicant_last_name: string
  applicant_first_name: string
  applicant_first_name_kana?: string
  applicant_last_name_kana?: string
  applicant_birthday: string
  applicant_phone: string
  applicant_email?: string
  applicant_postal_code: string | null
  applicant_address: string | null
  registration_card_number: string | null
  status: ReservationStatusLabelsKeys
  settlement_method: SettlementNameProps | null
  settlement_method_label: string | null
  saves_data: boolean
  settlement_status: BillingStatusLabelsKeys
  how_to_receive_medicine?: 'visit' | 'delivery'
  prescription_medicine: PrescriptionType | null
  pharmacy_service: PharmacyServiceType | null
  pharmacy?: PharmacyProps
  created_at: string
  updated_at: string
  updated_by: UpdatedByLabelsKeys
  slip_numbers?: string[]
  verification_type: VerificationTypes
  online_eligibility_verification_agreed: boolean
  is_phr_integration: boolean
  soh_access_url: string | null
} & UploadImagesProps

export type PreReservationListItemProps = {
  id: number
  applicant_phone?: string
  applicant_email?: string
  reservation_department: string | null
  provisional_reservation_datetime_start: string | null
  provisional_reservation_datetime_end: string | null
  applicant_name: string | null
  access_key_expiration: string
}

export type PreReservation = {
  id: number
  applicant_phone?: string
  applicant_email?: string
  provisional_reservation_access_key: string
  access_key_expiration: string
  provisional_reservation_department_id: number | null
  provisional_reservation_datetime_start: string | null
  provisional_reservation_datetime_end: string | null
  applicant_name: string | null
  sms_last_sent_at: string
  created_at: string
  updated_at: string
}

export type PreReservationForm = {
  applicant_phone?: string
  applicant_email?: string
  department_id?: number
  provisional_reservation_datetime_start?: string
  provisional_reservation_datetime_end?: string
  applicant_name?: string
}

export type Billing = {
  billing_date?: string
  e_rx_number: string | null // NOTE: 電子処方箋引換番号
  amount: number
  detail?: string
  attachment_images?: ImageProps[]
  error_type?: string | null
  error_message?: string | null
}

export type BillingForm = {
  id?: number
  settlement_method?: SettlementNameProps
  e_rx_number?: string | null // NOTE: 電子処方箋引換番号
  amount: number
  detail?: string
  attachment_images?: ImageProps[]
}

export type UpdatePasswordParams = {
  old_password: string
  new_password: string
}

export type OAuthTarget = 'stripe' | 'teams' | 'zoom'

export type ContactEmails = { email: string }[]

export type PublicInformation = {
  id?: number
  is_request: boolean
  contact_name?: string | null
  institution_url?: string | null
  is_possible_first_visit?: boolean | null
  classification?: string | null
  characteristic?: string | null
  message?: string | null
  reservation_method?: string | null
  reservation_method_free_description?: string | null
}

export type PrescriptionType = 'direct' | 'pharmacy' | 'own'
export type PrescriptionSetting = {
  prescription_medicine: PrescriptionType | null
  is_todokusuri: boolean | null
  server_error?: boolean
}

export type InformationToMed = { title: string; url: string }

export type SaluDiUserAccessCodeParams = {
  access_code: string
}
